"use client";

import {
  ActivityIcon,
  ArrowUpIcon,
  BrainIcon,
  ChartNoAxesCombinedIcon,
  ChevronDownIcon,
  DatabaseIcon,
  FilesIcon,
  InfoIcon,
  KanbanSquareIcon,
  LinkIcon,
  LockIcon,
  type LucideIcon,
  PackageOpenIcon,
  PlayIcon,
  RouteIcon,
  Table2Icon,
  XIcon,
} from "lucide-react";
import {
  type ColumnDef,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { type ReactNode, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

import { useStreamingMarkdownRehypePlugins } from "@product/components/app/assistant-response-streaming";
import {
  markdownRehypePlugins,
  markdownRemarkPlugins,
} from "@product/components/content/markdown-math";
import { normalizeMarkdownContent } from "@product/components/content/markdown-normalize";
import { staticMarkdownRendererComponents } from "@product/components/content/markdown-renderer-components-static";

/**
 * Real product surfaces, imported by source out of `apps/app` through the
 * bridge seam (see `components/editorial/product-bridge`). The table grid is
 * the application's own virtualized grid — sticky header, zebra rows
 * composited onto `--surface`, fluid column widths — driven here by a static
 * TanStack table instance over the fixture records. The dashboard widgets are
 * the application's own chart components on recharts, painting the product's
 * `--chart-*` hues, which the bridge now carries.
 */
import type { ChannelFeedEngagement } from "@mlx/contracts";
import { ChannelDataFreshnessIndicator } from "@product/app/workspace/channels/[channelId]/channel-data-freshness-indicator";
import { ChannelFeedEngagementSummary } from "@product/app/workspace/channels/[channelId]/channel-feed-engagement";
import { DashboardCategoryChart, DashboardRadialChart } from "@product/app/workspace/channels/[channelId]/record-dashboard-category-charts";
import type { ChartPoint } from "@product/app/workspace/channels/[channelId]/record-dashboard-model";
import { DashboardWidget } from "@product/app/workspace/channels/[channelId]/record-dashboard-shell";
import { RecordTableGrid } from "@product/app/workspace/channels/[channelId]/record-table-grid";
import { BoardCardView, BoardColumnView } from "@product/app/workspace/channels/[channelId]/record-board-view";
import { MENTION_ENTITY_STYLES } from "@product/components/app/entity-mention-input";
import {
  type ProvenanceActor,
  ProvenanceAvatarStack,
} from "@product/components/app/provenance-avatar-stack";
import { RunSourcesUsedView } from "@product/components/app/run-sources-used-view";

import { DATA_LABEL_CLS, DISPLAY, LABEL_CLS, ProductBridge } from "@/components/editorial";

import type { ConceptGroup } from "../spec";
import {
  CHANNEL_FIXTURE,
  FRESHNESS_FRESH,
  FRESHNESS_STALE,
  RECEIPT_PLUGINS,
  RECEIPT_SOURCES,
  RECEIVABLES_RECEIPT,
  RENEWAL_DASHBOARD,
  RENEWAL_RECORDS,
  type RenewalRecord,
} from "./product-fixtures";

/**
 * Channels, Data Sets and views — real where the seam allows, reconstructed
 * where it does not, and each exhibit says which is which.
 *
 * The application's channel *surface* is driven by Electric subscriptions and
 * cannot cross into the marketing site, but its presentational layers can: the
 * Table view renders through the product's own `RecordTableGrid`, and the
 * Dashboard through the product's own widget and chart components, both fed by
 * static fixtures instead of live subscriptions. The chrome around them
 * (toolbar, section nav, view picker) and the Board columns remain
 * reconstructions from `product-fixtures.ts`, rendered inside `ProductBridge`
 * so everything carries the product's tokens and face (Inter) on the editorial
 * ground.
 *
 * Every string is a literal: channel name, section names, view names, freshness
 * labels and reason codes come from the fixture or from the product's own
 * freshness presentation table. Every figure is labelled illustrative. The
 * renewal accounts are fictional, made for concept review.
 */

const INK_58 = "text-[color-mix(in_srgb,var(--theme-ink)_58%,transparent)]";
const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_80 = "text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";

/* Ground inks for surface:"dark" sheets — the cream used as ink on the deep
   plate, matching the board's other dark specs. Rules flip on their own via
   the frame's DARK_TOKENS; only text needs the swap. */
const GINK_58 = "text-[color-mix(in_srgb,var(--theme-ground)_58%,transparent)]";
const GINK_72 = "text-[color-mix(in_srgb,var(--theme-ground)_72%,transparent)]";
const GINK_82 = "text-[color-mix(in_srgb,var(--theme-ground)_82%,transparent)]";

/** The Data Set behind all three views, named by its own dashboard tile. */
const DATA_SET_NAME = RENEWAL_DASHBOARD.tiles[1].label;
const RECORD_COUNT = RENEWAL_RECORDS.length;

const SECTION_ICONS: Record<string, LucideIcon> = {
  "Data sets": DatabaseIcon,
  Feed: ActivityIcon,
  Sources: LinkIcon,
  Memory: BrainIcon,
  Files: FilesIcon,
  Artifacts: PackageOpenIcon,
  "About this channel": InfoIcon,
};

const VIEW_ICONS: Record<string, LucideIcon> = {
  Table: Table2Icon,
  Board: KanbanSquareIcon,
  Dashboard: ChartNoAxesCombinedIcon,
};

/** Pipeline order, so the Board and the value-by-stage bars agree. */
const STAGE_ORDER: RenewalRecord["stage"][] = [
  "Discovery",
  "Validation",
  "Commercial",
  "Contracting",
  "Renewed",
];

/** Product status colouring: identity of the health option, not brand accent. */
const HEALTH_CHIP: Record<RenewalRecord["health"], string> = {
  "On track": "bg-emerald-500/10 text-emerald-700",
  Watch: "bg-amber-500/15 text-amber-700",
  "At risk": "bg-red-500/10 text-red-700",
};

const HEALTH_INK: Record<string, string> = {
  "On track": "text-emerald-500",
  Watch: "text-amber-500",
  "At risk": "text-red-500",
};

/**
 * The product's own freshness presentation, transcribed from
 * `channelDataFreshnessPresentation` in
 * apps/app/src/app/workspace/channels/[channelId]/channel-data-freshness-indicator.tsx.
 */
const FRESHNESS_STATES = {
  fresh: {
    label: "Data is fresh",
    summary: "The displayed data is within its freshness target.",
    dot: "bg-emerald-500 ring-emerald-500/20",
  },
  stale: {
    label: "Data is stale",
    summary: "The displayed data is older than its freshness target.",
    dot: "bg-red-500 ring-red-500/20",
  },
} as const;

/* ================================================================= */
/*  Editorial framing                                                 */
/* ================================================================= */

function ConceptHeader({
  marker,
  title,
  body,
  reading,
  dark = false,
}: {
  marker: string;
  title: string;
  body: string;
  reading: string;
  dark?: boolean;
}) {
  const body72 = dark ? GINK_72 : INK_72;
  const label58 = dark ? GINK_58 : INK_58;
  return (
    <header className="grid gap-6 border-b border-dotted border-[var(--rule)] pb-7 md:grid-cols-[minmax(0,1.05fr)_minmax(18rem,0.95fr)] md:items-end">
      <div>
        <p
        className={`${LABEL_CLS} ${dark ? "text-[var(--theme-accent)]" : "text-[var(--theme-accent-text)]"}`}
      >
        {marker}
      </p>
        <h3
          style={DISPLAY}
          className="mt-3 max-w-[18ch] text-[clamp(1.85rem,4vw,3.25rem)] leading-[0.99] font-semibold tracking-[-0.045em]"
        >
          {title}
        </h3>
      </div>
      <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_minmax(9.5rem,0.52fr)] sm:items-end">
        <p className={`max-w-[48ch] text-[0.95rem] leading-[1.6] ${body72}`}>{body}</p>
        <aside className="border-l border-[var(--rule)] pl-3">
          <p
            className={`text-[0.6rem] font-semibold tracking-[0.16em] uppercase ${label58}`}
          >
            Reading
          </p>
          <p className="mt-1 text-[0.82rem] leading-[1.4] font-semibold">{reading}</p>
        </aside>
      </div>
    </header>
  );
}

function ExhibitHeader({
  left,
  right,
  dark = false,
}: {
  left: string;
  right: string;
  dark?: boolean;
}) {
  return (
    <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-md:flex-col max-md:items-start max-md:gap-1">
      <span className={`${dark ? GINK_82 : INK_80} ${DATA_LABEL_CLS}`}>{left}</span>
      <strong
        className={`${dark ? "text-[var(--theme-accent)]" : "text-[var(--theme-accent-text)]"} ${DATA_LABEL_CLS}`}
      >
        {right}
      </strong>
    </div>
  );
}

function ExhibitFooter({
  children,
  dark = false,
}: {
  children: ReactNode;
  dark?: boolean;
}) {
  return (
    <div
      className={`border-t border-dotted border-[var(--rule)] px-4 py-[0.7rem] ${dark ? GINK_82 : INK_80} text-[0.72rem] leading-[1.45]`}
    >
      {children}
    </div>
  );
}

/** The small state label above a specimen inside an exhibit. */
function StateLabel({ children }: { children: string }) {
  return <p className={`${INK_80} ${DATA_LABEL_CLS} mb-2.5`}>{children}</p>;
}

/**
 * The named working context, printed as a ledger. Channel, Data Set and view
 * are the three nouns the product never leaves implicit.
 */
function ContextLedger({ view, dark = false }: { view: string; dark?: boolean }) {
  const rows: [string, string][] = [
    ["Channel", CHANNEL_FIXTURE.name],
    ["Data Set", `${DATA_SET_NAME} · ${RECORD_COUNT} records`],
    ["View", view],
  ];
  return (
    <dl className="m-0 grid sm:grid-cols-3">
      {rows.map(([label, value]) => (
        <div
          key={label}
          className="border-b border-dotted border-[var(--rule)] px-4 py-2 last:border-b-0 sm:border-r sm:border-b-0 sm:last:border-r-0"
        >
          <dt className={`${dark ? GINK_58 : INK_58} ${DATA_LABEL_CLS}`}>{label}</dt>
          <dd className="m-0 mt-0.5 text-[0.8rem] leading-[1.35] font-medium">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ================================================================= */
/*  Reconstructed product surfaces (rendered inside ProductBridge)    */
/* ================================================================= */

/**
 * The toolbar dot is the real ChannelDataFreshnessIndicator, fed the fixture
 * freshness for the requested state — click it and the product's own popover
 * opens with the real facts table. The static FreshnessPopover further down
 * remains the print-legible rendering of the same rows.
 */
function FreshnessDot({ state }: { state: keyof typeof FRESHNESS_STATES }) {
  return (
    <ChannelDataFreshnessIndicator
      freshness={state === "fresh" ? FRESHNESS_FRESH : FRESHNESS_STALE}
    />
  );
}

function ToolbarIconButton({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <span
      className="inline-flex size-7 shrink-0 items-center justify-center border border-border bg-card"
      title={label}
    >
      <Icon className="size-3.5 text-foreground" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Channel name · freshness dot · Data sets · Trace data · Channel access. */
function ChannelToolbar({ state }: { state: keyof typeof FRESHNESS_STATES }) {
  return (
    <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3">
      <p className="min-w-0 truncate text-sm font-medium text-foreground">
        {CHANNEL_FIXTURE.name}
      </p>
      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        <FreshnessDot state={state} />
        <span aria-hidden="true" className="h-5 w-px shrink-0 bg-border" />
        <ToolbarIconButton icon={DatabaseIcon} label="Data sets" />
        <span className="inline-flex h-7 shrink-0 items-center gap-1.5 border border-border bg-card px-1.5 text-xs font-medium text-foreground">
          <RouteIcon className="size-3.5" aria-hidden="true" />
          <span className="max-sm:hidden">Trace data</span>
        </span>
        <ToolbarIconButton icon={LockIcon} label="Channel access" />
      </div>
    </header>
  );
}

/**
 * The channel section nav. "Start agent session" leads, in the product's own
 * primary ink — the one claret element in CHV-001.
 */
function ChannelSectionNav({ selected }: { selected: string }) {
  return (
    <nav
      aria-label="Channel sections"
      className="flex h-11 shrink-0 items-center gap-4 overflow-x-auto border-b border-border px-3 text-sm"
    >
      <span className="inline-flex shrink-0 items-center gap-1.5 font-medium text-primary">
        <PlayIcon className="size-3.5 fill-current" aria-hidden="true" />
        Start agent session
      </span>
      {CHANNEL_FIXTURE.sections.map((section) => {
        const Icon = SECTION_ICONS[section] ?? InfoIcon;
        const active = section === selected;
        return (
          <span
            key={section}
            className={`inline-flex shrink-0 items-center gap-1.5 ${active ? "font-medium text-foreground" : "text-muted-foreground"}`}
          >
            <Icon className="size-3.5" aria-hidden="true" />
            {section}
          </span>
        );
      })}
    </nav>
  );
}

/** The view picker trigger, plus the record count the filter bar reports. */
function ViewPickerBar({ name, kind }: { name: string; kind: string }) {
  const Icon = VIEW_ICONS[kind] ?? Table2Icon;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
      <span className="inline-flex h-7 max-w-full items-center gap-1.5 border border-border bg-card px-2 text-xs font-medium text-foreground">
        <Icon className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 truncate">{name}</span>
        <ChevronDownIcon
          className="size-3.5 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
      </span>
      <span className="text-xs text-muted-foreground">
        {DATA_SET_NAME} · {RECORD_COUNT} records
      </span>
    </div>
  );
}

/** The option chip's dark-appearance colours, as the product's dark: variants set them. */
const HEALTH_CHIP_DARK: Record<RenewalRecord["health"], string> = {
  "On track": "bg-emerald-500/15 text-emerald-400",
  Watch: "bg-amber-500/20 text-amber-300",
  "At risk": "bg-red-500/15 text-red-400",
};

function HealthChip({
  health,
  dark = false,
}: {
  health: RenewalRecord["health"];
  dark?: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-[0.68rem] leading-[1.3] font-medium whitespace-nowrap ${
        dark ? HEALTH_CHIP_DARK[health] : HEALTH_CHIP[health]
      }`}
    >
      {health}
    </span>
  );
}

/**
 * Column set for the real grid, mirroring the saved View's field order. Sizes
 * follow the product's `defaultFieldWidth` steps (date 140, number/currency/
 * percent 120, select 150, text 160), tightened only where a column would
 * otherwise force the specimen to scroll; the grid's fluid widths stretch them
 * to fill the exhibit exactly as they fill the application. Cell *content* is
 * the board's own (the product's field renderers read live option schemas);
 * the grid around it — sticky header, zebra compositing, virtualized rows —
 * is the imported component.
 */
function realTableColumns(dark: boolean): ColumnDef<RenewalRecord>[] {
  return [
  {
    id: "title",
    header: "Title",
    size: 168,
    cell: ({ row }) => (
      <span className="truncate font-medium text-foreground">
        {row.original.title}
      </span>
    ),
  },
  {
    id: "health",
    header: "Health",
    size: 104,
    cell: ({ row }) => <HealthChip health={row.original.health} dark={dark} />,
  },
  {
    id: "annualValue",
    header: "Annual value",
    size: 104,
    cell: ({ row }) => (
      <span className="truncate tabular-nums text-foreground">
        {row.original.annualValue}
      </span>
    ),
  },
  {
    id: "renewalDate",
    header: "Renewal date",
    size: 112,
    cell: ({ row }) => (
      <span className="truncate text-muted-foreground">
        {row.original.renewalDate}
      </span>
    ),
  },
  {
    id: "probability",
    header: "Renewal probability",
    size: 128,
    cell: ({ row }) => (
      <span className="truncate tabular-nums text-foreground">
        {row.original.probability}
      </span>
    ),
  },
  {
    id: "accountLead",
    header: "Account lead",
    size: 116,
    cell: ({ row }) => (
      <span className="truncate text-muted-foreground">
        {row.original.accountLead}
      </span>
    ),
  },
  {
    id: "nextStep",
    header: "Next step",
    size: 176,
    cell: ({ row }) => (
      <span className="truncate text-muted-foreground">
        {row.original.nextStep}
      </span>
    ),
  },
  {
    id: "stage",
    header: "Stage",
    size: 104,
    cell: ({ row }) => (
      <span className="truncate text-foreground">{row.original.stage}</span>
    ),
  },
  ];
}

const REAL_TABLE_COLUMNS = realTableColumns(false);
const REAL_TABLE_COLUMNS_DARK = realTableColumns(true);

/**
 * The Table view over the Data Set — the application's own RecordTableGrid,
 * driven by a static TanStack instance. Resizing is off (nothing should
 * invite a drag on a printed exhibit), and the twelve fixture rows sit inside
 * the virtualizer's initial overscan, so every record renders even before the
 * grid measures its viewport.
 */
export function RecordTable({ dark = false }: { dark?: boolean }) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const table = useReactTable({
    data: RENEWAL_RECORDS,
    columns: dark ? REAL_TABLE_COLUMNS_DARK : REAL_TABLE_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: false,
  });

  return (
    <div className="flex max-h-[26.5rem] min-w-0 flex-col">
      <RecordTableGrid table={table} scrollRef={scrollRef} empty={false} />
    </div>
  );
}

/** The three-column set for the compact panel in the three-up comparison. */
const COMPACT_TABLE_COLUMNS: ColumnDef<RenewalRecord>[] = [
  {
    id: "title",
    header: "Title",
    size: 132,
    cell: ({ row }) => (
      <span className="flex min-w-0 items-center gap-1.5 text-foreground">
        <span
          aria-hidden="true"
          className={`size-1.5 shrink-0 rounded-full bg-current ${HEALTH_INK[row.original.health]}`}
        />
        <span className="truncate">{row.original.title}</span>
      </span>
    ),
  },
  {
    id: "annualValue",
    header: "Annual value",
    size: 92,
    cell: ({ row }) => (
      <span className="truncate tabular-nums text-foreground">
        {row.original.annualValue}
      </span>
    ),
  },
  {
    id: "stage",
    header: "Stage",
    size: 88,
    cell: ({ row }) => (
      <span className="truncate text-muted-foreground">{row.original.stage}</span>
    ),
  },
];

/** The compact Table in the three-up comparison — the same real grid, narrower. */
export function RecordTableCompact() {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const table = useReactTable({
    data: RENEWAL_RECORDS,
    columns: COMPACT_TABLE_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: false,
  });

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <RecordTableGrid table={table} scrollRef={scrollRef} empty={false} />
    </div>
  );
}

/**
 * The Board view: the same records, grouped by stage. Five stage columns hold a
 * fixed width and scroll inside the panel on small screens; from `lg` they
 * share the panel evenly so nothing is ever clipped at desktop.
 */
function RecordBoard() {
  return (
    <div className="flex-1 overflow-x-auto bg-muted/40 p-2 lg:overflow-x-hidden">
      <div className="flex min-w-max gap-1.5 lg:min-w-0">
        {STAGE_ORDER.map((stage) => {
          const records = RENEWAL_RECORDS.filter((record) => record.stage === stage);
          return (
            <section
              key={stage}
              className="w-[5.75rem] shrink-0 lg:w-auto lg:min-w-0 lg:flex-1"
            >
              <header className="flex items-baseline justify-between gap-1 px-0.5 pb-1.5">
                <h4 className="min-w-0 truncate text-[0.62rem] font-medium text-foreground">
                  {stage}
                </h4>
                <span className="text-[0.62rem] tabular-nums text-muted-foreground">
                  {records.length}
                </span>
              </header>
              <ul className="m-0 grid list-none gap-1.5 p-0">
                {records.map((record) => (
                  <li
                    key={record.title}
                    className="border border-border bg-card px-1.5 py-1.5"
                  >
                    <p className="text-[0.62rem] leading-[1.3] font-medium text-foreground">
                      {record.title}
                    </p>
                    <p className="mt-1 flex items-center gap-1 text-[0.6rem] text-muted-foreground">
                      <span
                        aria-hidden="true"
                        className={`size-1.5 shrink-0 rounded-full bg-current ${HEALTH_INK[record.health]}`}
                      />
                      <span className="tabular-nums">{record.annualValue}</span>
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}

/** The Data Set aggregated for the real chart components, pipeline order. */
const STAGE_VALUE_POINTS: ChartPoint[] = STAGE_ORDER.map((stage) => ({
  label: stage,
  value:
    RENEWAL_DASHBOARD.valueByStage.find((item) => item.stage === stage)?.value ??
    0,
}));

const HEALTH_POINTS: ChartPoint[] = RENEWAL_DASHBOARD.health.map((slice) => ({
  label: slice.label,
  value: slice.count,
}));

/**
 * The Dashboard view: reconstructed metric tiles over the application's own
 * chart widgets. DashboardWidget and both charts are the imported product
 * components; each sits in an explicit-height wrapper because in the
 * application that height comes from the dashboard layout grid, which stays
 * behind (it is drag-and-drop machinery, pointless on a still exhibit). The
 * radial slices follow the product's palette order — the dashboard colours
 * series by position, not by option identity.
 */
function RecordDashboard() {
  return (
    <div className="grid gap-2 p-2">
      <dl className="m-0 grid grid-cols-3 gap-px bg-border">
        {RENEWAL_DASHBOARD.tiles.map((tile) => (
          <div key={tile.label} className="bg-card px-2 py-2">
            <dt className="text-[0.62rem] leading-[1.3] text-muted-foreground">
              {tile.label}
            </dt>
            <dd className="m-0 mt-1 text-[0.95rem] leading-none font-semibold tabular-nums text-foreground">
              {tile.value}
            </dd>
            <p className="mt-1 text-[0.6rem] leading-[1.3] text-muted-foreground">
              {tile.detail}
            </p>
          </div>
        ))}
      </dl>

      <div className="grid min-w-0 gap-2 sm:grid-cols-2">
        <div className="h-[240px] min-w-0">
          <DashboardWidget title="Annual value by stage" unit="GBP" meta="Sum">
            <DashboardCategoryChart
              title="Annual value by stage"
              data={STAGE_VALUE_POINTS}
              displayKind={{ type: "currency", currencyCode: "GBP" }}
              seriesName="Annual value"
            />
          </DashboardWidget>
        </div>

        <div className="h-[240px] min-w-0">
          <DashboardWidget title="Health mix" meta="Records · Count">
            <DashboardRadialChart
              title="Health mix"
              data={HEALTH_POINTS}
              variant="donut"
            />
          </DashboardWidget>
        </div>
      </div>
    </div>
  );
}

/** One panel in the three-up comparison. */
function ViewPanel({
  name,
  kind,
  children,
}: {
  name: string;
  kind: string;
  children: ReactNode;
}) {
  const Icon = VIEW_ICONS[kind] ?? Table2Icon;
  return (
    <section className="flex min-w-0 flex-col border border-border bg-card">
      <header className="flex h-9 shrink-0 items-center gap-1.5 border-b border-border px-2">
        <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <p className="min-w-0 truncate text-xs font-medium text-foreground">{name}</p>
        <span className="ml-auto shrink-0 text-[0.62rem] text-muted-foreground">
          {kind}
        </span>
      </header>
      <div className="flex min-w-0 flex-1 flex-col">{children}</div>
    </section>
  );
}

/** The freshness popover, row for row. */
function FreshnessPopover({
  state,
  rows,
}: {
  state: keyof typeof FRESHNESS_STATES;
  rows: readonly (readonly [string, string])[];
}) {
  const presentation = FRESHNESS_STATES[state];
  return (
    <section
      aria-label="Data freshness"
      className="w-full max-w-[22rem] border border-border bg-popover text-popover-foreground shadow-[0_10px_28px_rgba(22,18,12,0.10)]"
    >
      <div className="flex items-center gap-2 px-2 py-2">
        <span
          aria-hidden="true"
          className={`size-2.5 shrink-0 rounded-full ring-4 ${presentation.dot}`}
        />
        <p className="min-w-0 text-sm font-medium text-foreground">
          {presentation.label}
        </p>
      </div>
      <p className="border-t border-border px-2 py-1.5 text-[0.7rem] leading-[1.45] text-muted-foreground">
        {presentation.summary}
      </p>
      <dl className="m-0 border-t border-border pb-1 text-xs">
        {rows.map(([label, value], index) => (
          <div
            key={label}
            className={`flex items-baseline justify-between gap-3 px-2 py-1.5 ${index % 2 === 0 ? "bg-foreground/[0.04]" : ""}`}
          >
            <dt className="shrink-0 text-muted-foreground">{label}</dt>
            <dd className="m-0 min-w-0 text-right tabular-nums text-foreground">
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/* ================================================================= */
/*  CHV-001 · One channel, one governed Data Set                      */
/* ================================================================= */

export function ChannelWorkspaceExhibit({ chrome = true }: { chrome?: boolean } = {}) {
  const tableView = CHANNEL_FIXTURE.views[0];
  return (
    <section>
      {chrome ? (
        <ConceptHeader
        marker="Channels · Data Sets · views"
        title="The working context is never implied."
        body="A channel holds a governed Data Set. A Data Set holds records with one schema. A view is a saved way of reading them. MLX keeps all three named on screen, so anyone looking at a number can say which records it came from and how they were filtered."
        reading="You always know what you are looking at."
      />
      ) : null}

      <figure className={`m-0 ${chrome ? "mt-7 border border-[var(--rule)]" : ""}`}>
        {chrome ? (
          <ExhibitHeader
          left="Exhibit — channel workspace, Table view"
          right="Real product grid"
        />
        ) : null}
        {chrome ? (
          <ContextLedger view={`${tableView.name} · ${tableView.kind}`} />
        ) : null}
        <ProductBridge className={chrome ? "border-t border-dotted border-[var(--rule)] p-3 sm:p-5" : "p-0"}>
          <div className="min-w-0 border border-border bg-card">
            <ChannelToolbar state="fresh" />
            <ChannelSectionNav selected="Data sets" />
            <ViewPickerBar name={tableView.name} kind={tableView.kind} />
            <RecordTable />
          </div>
        </ProductBridge>
        {chrome ? (
          <ExhibitFooter>
          The table is the application's own RecordTableGrid — sticky header, zebra
          rows, fluid columns — imported from apps/app and driven by a static table
          instance over the fixture records; cell text is set by the board because the
          product's field renderers read live option schemas. The toolbar, section nav
          and view picker above it are reconstructions. Renewal accounts are
          fictional, made for concept review. Figures are illustrative.
        </ExhibitFooter>
        ) : null}
      </figure>
    </section>
  );
}

/* ================================================================= */
/*  CHV-002 · One Data Set, many views                                */
/* ================================================================= */

function ManyViewsExhibit() {
  const [table, board, dashboard] = CHANNEL_FIXTURE.views;
  return (
    <section>
      <ConceptHeader
        marker="One Data Set, many views"
        title="Same records. Three ways of reading them."
        body="A view does not copy records; it renders them. Change the lens and the numbers cannot drift, because there is only one set of records underneath and one schema governing it. Table, Board and Dashboard are three saved views over the same Data Set."
        reading="Switch the lens without forking the truth."
      />

      <figure className="m-0 mt-7 border border-[var(--rule)]">
        <ExhibitHeader
          left="Exhibit — three saved views, one Data Set"
          right="Illustrative specimen"
        />
        <ProductBridge className="p-3 sm:p-5">
          <div className="flex flex-wrap items-center gap-2 pb-3">
            <span className="inline-flex items-center bg-primary px-2 py-1 text-[0.68rem] leading-none font-medium text-primary-foreground">
              {DATA_SET_NAME}
            </span>
            <span className="text-xs text-muted-foreground">
              {RECORD_COUNT} records · {CHANNEL_FIXTURE.name}
            </span>
          </div>
          <div className="grid min-w-0 gap-3 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.55fr)]">
            <ViewPanel name={table.name} kind={table.kind}>
              <RecordTableCompact />
            </ViewPanel>
            <ViewPanel name={board.name} kind={board.kind}>
              <RecordBoard />
            </ViewPanel>
            {/* Full width: the real chart components need the frame width the
                application's dashboard grid would give them. */}
            <div className="min-w-0 lg:col-span-2">
              <ViewPanel name={dashboard.name} kind={dashboard.kind}>
                <RecordDashboard />
              </ViewPanel>
            </div>
          </div>
        </ProductBridge>
        <ExhibitFooter>
          All three panels are rendered from one array of twelve records in
          apps/web/src/app/concept/groups/product-fixtures.ts — the Board groups it by
          stage and the Dashboard aggregates it, so the claim is demonstrated on the
          page rather than asserted in the caption. The Table panel is the
          application's own grid and the Dashboard's two charts are its own widget
          components on the product's chart hues; the Board columns are
          reconstructions. Renewal accounts are fictional, made for concept review.
          Figures are illustrative.
        </ExhibitFooter>
      </figure>
    </section>
  );
}

/* ================================================================= */
/*  CHV-003 · Know how old the number is                              */
/* ================================================================= */

function FreshnessExhibit() {
  return (
    <section>
      <ConceptHeader
        marker="Freshness"
        title="Know how old the number is."
        body="A channel carries a freshness state on its toolbar, and the dot opens on the evidence behind it: when the data is as of, when the source last landed, when the model last refreshed, when the next run is due, and why the state is what it is."
        reading="A number you can date is a number you can use."
      />

      <figure className="m-0 mt-7 border border-[var(--rule)]">
        <ExhibitHeader
          left="Exhibit — freshness state and its evidence"
          right="Real product indicator"
        />
        <ProductBridge className="p-3 sm:p-5">
          <div className="grid min-w-0 gap-5 md:grid-cols-2">
            <div className="min-w-0">
              <StateLabel>Toolbar, then the popover</StateLabel>
              <div className="min-w-0 border border-border bg-card">
                <ChannelToolbar state="fresh" />
                <div className="flex justify-end p-3">
                  <FreshnessPopover
                    state="fresh"
                    rows={CHANNEL_FIXTURE.freshness.rows}
                  />
                </div>
              </div>
            </div>
            <div className="min-w-0">
              <StateLabel>A second state, same surface</StateLabel>
              <div className="min-w-0 border border-border bg-card">
                <ChannelToolbar state="stale" />
                <div className="flex justify-end p-3">
                  <FreshnessPopover
                    state="stale"
                    rows={[["Why", "Source data is beyond its freshness target"]]}
                  />
                </div>
              </div>
              <p className="mt-2.5 text-[0.7rem] leading-[1.5] text-muted-foreground">
                The same five labels are always present. The state, the values and the
                reason are what change.
              </p>
            </div>
          </div>
        </ProductBridge>
        <ExhibitFooter>
          <span className="flex items-start gap-2.5">
            <span
              aria-hidden="true"
              className="mt-[0.3rem] size-2 shrink-0 bg-[var(--theme-dark)]"
            />
            <span>
              Last ingestion is the connector sync. Projection refresh is the model
              refresh applying approved definitions to that data. They run on separate
              schedules, and the popover reports both — which is why a channel can hold
              recent source data and an older projection at the same time. The toolbar
              dot is the real ChannelDataFreshnessIndicator — click it for the
              product's own popover; the printed popovers below it transcribe the same
              rows for reading. Figures are illustrative.
            </span>
          </span>
        </ExhibitFooter>
      </figure>
    </section>
  );
}

/* ================================================================= */
/*  CHV-004 · Stop the sprawl at the schema                           */
/* ================================================================= */

/**
 * The unstructured world, drawn: generic slips in the editorial idiom, no
 * third-party chrome. Slack and Notion are named in the prose as facts of
 * where output lands, not imitated as interfaces.
 */
export const SPRAWL_SLIPS: { place: string; body: string; meta: string }[] = [
  {
    place: "#renewals",
    body: "AI weekly renewal summary — 340 words, verdicts in prose",
    meta: "Mon 09:12 · version 1 of 3",
  },
  {
    place: "thread reply",
    body: "“updated numbers below — ignore the table above”",
    meta: "6 replies deep",
  },
  {
    place: "#general",
    body: "the same table again, pasted as a screenshot",
    meta: "Wed 16:40 · unsearchable",
  },
  {
    place: "notes page",
    body: "“Renewal review FINAL v3”",
    meta: "edited 3 weeks ago",
  },
  {
    place: "direct message",
    body: "“which one of these is current?”",
    meta: "Fri 17:55 · unanswered",
  },
];

function SchemaVsSprawlExhibit() {
  return (
    <section>
      <ConceptHeader
        marker="Records · schemas"
        title="Stop the sprawl at the schema."
        body="The more a team uses AI, the more it produces — and unstructured output compounds. Summaries land in Slack threads, tables in screenshots, conclusions in a notes page named FINAL v3, and maintaining company knowledge becomes the overhead. In MLX you define the schema instead, and agents must file typed records into it: the same week of output, findable, comparable and current."
        reading="AI that files records, not threads."
      />

      <figure className="m-0 mt-7 border border-[var(--rule)]">
        <ExhibitHeader
          left="Exhibit — the same week of agent output, twice"
          right="Drawn sprawl · real grid"
        />
        <div className="grid md:grid-cols-2">
          <div className="min-w-0 border-b border-dotted border-[var(--rule)] p-4 sm:p-5 md:border-r md:border-b-0">
            <StateLabel>A · Unstructured — where it usually lands</StateLabel>
            <ul className="m-0 grid list-none gap-2 p-0">
              {SPRAWL_SLIPS.map((slip, index) => (
                <li
                  key={slip.place}
                  className={`border border-dotted border-[var(--rule)] px-3 py-2 ${
                    index % 2 === 1 ? "ml-4 sm:ml-7" : "mr-4 sm:mr-7"
                  }`}
                >
                  <p className={`${INK_58} ${DATA_LABEL_CLS}`}>{slip.place}</p>
                  <p className={`mt-0.5 ${INK_80} text-[0.82rem] leading-[1.5]`}>
                    {slip.body}
                  </p>
                  <p className={`mt-0.5 ${INK_58} text-[0.68rem] leading-[1.4]`}>
                    {slip.meta}
                  </p>
                </li>
              ))}
            </ul>
            <p
              className={`mt-3 border-t border-dotted border-[var(--rule)] pt-2 ${INK_72} text-[0.72rem] leading-[1.5]`}
            >
              5 places · 3 versions of the truth · 0 schema. Every future
              question starts with an archaeology dig.
            </p>
          </div>
          <div className="min-w-0 p-4 sm:p-5">
            <StateLabel>B · Schema-governed — where the Twin files it</StateLabel>
            <ProductBridge className="border border-border">
              <div className="flex min-w-0 flex-col">
                <RecordTableCompact />
              </div>
            </ProductBridge>
            <p className={`mt-3 ${INK_72} text-[0.72rem] leading-[1.5]`}>
              12 records · 1 schema · every field typed. The agent's verdicts
              land as Health, Next step and Stage — queryable by the next run,
              readable by the next person, one current version by construction.
            </p>
          </div>
        </div>
        <ExhibitFooter>
          Side A is drawn in the editorial idiom — no third-party interface is imitated; Slack and Notion are named in the prose as places output lands. Side B is the application's own grid over the same fixture records. Renewal accounts are fictional, made for concept review.
        </ExhibitFooter>
      </figure>
    </section>
  );
}

/* ================================================================= */
/*  CHV-005 · The schema is the contract                              */
/* ================================================================= */

/** The object type behind every renewal grid on this board. */
const SCHEMA_FIELDS: [string, string, string][] = [
  ["Title", "text", "required"],
  ["Health", "select", "On track · Watch · At risk"],
  ["Annual value", "currency", "GBP"],
  ["Renewal date", "date", ""],
  ["Renewal probability", "percent", "0–100"],
  ["Account lead", "user", "member directory"],
  ["Next step", "text", "one line"],
  ["Stage", "select", "5 stages, pipeline order"],
];

function SchemaContractExhibit() {
  return (
    <section>
      <ConceptHeader
        marker="Records · the write path"
        title="The schema is the contract."
        body="A schema in MLX is not a convention agents are asked to follow — it is the only door. Every record an agent creates or updates is checked against it on the way in: what fits is filed, what does not is turned away with nothing written. Define the shape once and every writer complies — the Twin, an automation, or Claude connected over MCP."
        reading="Define once. Every agent complies."
      />

      <figure className="m-0 mt-7 border border-[var(--rule)]">
        <ExhibitHeader
          left="Exhibit — one object type, and what happens at its door"
          right="The only door"
        />
        <div className="grid md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
          <div className="min-w-0 border-b border-dotted border-[var(--rule)] p-4 sm:p-5 md:border-r md:border-b-0">
            <StateLabel>Object type · Renewal account</StateLabel>
            <dl className="m-0 border border-dotted border-[var(--rule)]">
              {SCHEMA_FIELDS.map(([field, type, rule], index) => (
                <div
                  key={field}
                  className={`grid grid-cols-[minmax(7rem,1fr)_5rem_minmax(0,1.2fr)] gap-2 px-3 py-1.5 ${
                    index > 0 ? "border-t border-dotted border-[var(--rule)]" : ""
                  }`}
                >
                  <dt className={`${INK_88} text-[0.8rem] leading-[1.5] font-medium`}>
                    {field}
                  </dt>
                  <dd className={`m-0 ${INK_58} ${DATA_LABEL_CLS} self-center`}>
                    {type}
                  </dd>
                  <dd className={`m-0 ${INK_72} text-[0.72rem] leading-[1.5] self-center`}>
                    {rule}
                  </dd>
                </div>
              ))}
            </dl>
            <p className={`mt-3 ${INK_72} text-[0.72rem] leading-[1.5]`}>
              This is the exact object type every renewal grid, board and
              dashboard on this board renders.
            </p>
          </div>
          <ProductBridge className="min-w-0 p-4 sm:p-5">
            <p className="mb-2.5 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
              What happens at the door
            </p>
            <div className="grid gap-3">
              <div className="rounded-lg border border-border/70 bg-surface px-3.5 py-3">
                <p className="text-[10px] font-medium tracking-[0.08em] text-muted-foreground uppercase">
                  Filed
                </p>
                <p className="mt-1.5 text-sm font-medium text-foreground">
                  Meridian Foods
                </p>
                <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                  Next step →{" "}
                  <span className="text-foreground">“Share ROI model”</span>
                  {" · "}Renewal probability →{" "}
                  <span className="tabular-nums text-foreground">78%</span>
                </p>
                <p className="mt-1.5 text-[11px] leading-4 text-muted-foreground">
                  by MLX · renewal-watch automation · fits the schema, so it is
                  in the register
                </p>
              </div>
              <div className="rounded-lg border border-border/70 bg-surface px-3.5 py-3">
                <p className="text-[10px] font-medium tracking-[0.08em] text-muted-foreground uppercase">
                  Refused
                </p>
                <div className="mt-1.5 border-l-2 border-destructive/50 pl-3">
                  <p className="text-[13px] leading-relaxed text-foreground">
                    “forecast_note” is not a field of Renewal account.
                  </p>
                  <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
                    Nothing was written. The agent can ask for the field to be
                    added — a schema change, reviewed like any other.
                  </p>
                </div>
              </div>
            </div>
          </ProductBridge>
        </div>
        <ExhibitFooter>
          The schema rows are the object type behind every renewal grid on this board; the filed and refused moments are drawn after the product's validation behaviour, wording illustrative. The write path underneath is five typed operations — twin_channel_record_create, _update, _get and the object-type and field creators, twin.write scope — transcribed in packages/contracts/src/twin-queries.ts and listed in the MCP register.
        </ExhibitFooter>
      </figure>
    </section>
  );
}

/* ================================================================= */
/*  CHV-006 · The View, in the Session                                */
/* ================================================================= */

const MAYA: ProvenanceActor = { label: "Maya Chen", imageUrl: null, initials: "MC" };
const SAM: ProvenanceActor = { label: "Sam Okafor", imageUrl: null, initials: "SO" };
const TWIN_ACTOR: ProvenanceActor = {
  label: "MLX",
  imageUrl: null,
  initials: "T",
  icon: "twin",
};

/** The product's own mention chip classes, on the entity type given. */
function Mention({
  type,
  children,
}: {
  type: keyof typeof MENTION_ENTITY_STYLES;
  children: string;
}) {
  return (
    <span
      className={`rounded-[4px] px-1 py-px font-medium ${MENTION_ENTITY_STYLES[type].inline}`}
    >
      {children}
    </span>
  );
}

/** The resource panel's column set — the mentioned View, three fields wide. */
const PANEL_COLUMNS: ColumnDef<RenewalRecord>[] = [
  {
    id: "title",
    header: "Title",
    size: 150,
    cell: ({ row }) => (
      <span className="flex min-w-0 items-center gap-1.5 text-foreground">
        <span
          aria-hidden="true"
          className={`size-1.5 shrink-0 rounded-full bg-current ${HEALTH_INK[row.original.health]}`}
        />
        <span className="truncate">{row.original.title}</span>
      </span>
    ),
  },
  {
    id: "renewalDate",
    header: "Renewal date",
    size: 108,
    cell: ({ row }) => (
      <span className="truncate text-muted-foreground">
        {row.original.renewalDate}
      </span>
    ),
  },
  {
    id: "probability",
    header: "Probability",
    size: 92,
    cell: ({ row }) => (
      <span className="truncate tabular-nums text-foreground">
        {row.original.probability}
      </span>
    ),
  },
];

/** CHV-006's answer as markdown, so it can stream through the real wipe. */
const VIEW_SESSION_ANSWER_MD = `Four renewals land in the next 30 days. Orbit Education and Alder & Co Logistics are already Renewed, and Brightwater Energy is on track at 86% for 22 September.

The one to watch is **Northstar Gridworks** — £84,000 renewing 14 September at **72%**, health At risk. The next step on the record: resolve the DPA redlines.`;

/**
 * Streams the answer through the product's own wipe ONCE, the first time it
 * scrolls into view — and only after it has been off-screen, so full-page
 * capture surfaces (which see everything at mount) and reduced motion keep
 * the settled turn. A hidden settled copy holds the height.
 */
function OneShotWipeAnswer({ source }: { source: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [revealed, setRevealed] = useState(source.length);
  const [run, setRun] = useState(0);
  const sawOutside = useRef(false);
  const played = useRef(false);
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (!entry.isIntersecting) {
          sawOutside.current = true;
          return;
        }
        if (!sawOutside.current || played.current) return;
        played.current = true;
        observer.disconnect();
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        setRun((count) => count + 1);
        setRevealed(0);
        ticker.current = setInterval(() => {
          setRevealed((current) => {
            const next = Math.min(current + 48, source.length);
            if (next >= source.length && ticker.current) {
              clearInterval(ticker.current);
              ticker.current = null;
            }
            return next;
          });
        }, 110);
      },
      { threshold: 0.35 },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (ticker.current) clearInterval(ticker.current);
    };
  }, [source]);

  const playing = revealed < source.length;
  const normalized = normalizeMarkdownContent(source.slice(0, revealed), {
    streaming: playing,
  });
  const streamingPlugins = useStreamingMarkdownRehypePlugins({
    enabled: playing,
    source: normalized,
    streamIdentity: `view-session-answer-${run}`,
  });
  const rehypePlugins = streamingPlugins
    ? [...streamingPlugins, ...markdownRehypePlugins]
    : markdownRehypePlugins;

  return (
    <div ref={ref} className="relative">
      <div className="invisible" aria-hidden="true">
        <div className="typeset typeset-chat max-w-[52ch] break-words whitespace-normal">
          <ReactMarkdown
            remarkPlugins={markdownRemarkPlugins}
            rehypePlugins={markdownRehypePlugins}
            components={staticMarkdownRendererComponents}
          >
            {normalizeMarkdownContent(source)}
          </ReactMarkdown>
        </div>
      </div>
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="typeset typeset-chat max-w-[52ch] break-words whitespace-normal"
          data-streaming={playing ? "true" : undefined}
        >
          <ReactMarkdown
            remarkPlugins={markdownRemarkPlugins}
            rehypePlugins={rehypePlugins}
            components={staticMarkdownRendererComponents}
          >
            {normalized}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

export function SessionWithViewExhibit({ chrome = true }: { chrome?: boolean } = {}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const table = useReactTable({
    data: RENEWAL_RECORDS,
    columns: PANEL_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: false,
  });

  return (
    <section>
      {chrome ? (
        <ConceptHeader
        marker="Sessions · Views"
        title="Mention a View. The agent gets your records."
        body="Type @ in the composer and the channel's Data Sets and saved Views are right there. Mention one and the Session's scope is pinned to it: the agent reads exactly the records the View shows, and the View opens beside the conversation — so you watch it work against the same rows you see."
        reading="The workspace is in the conversation."
      />
      ) : null}

      <figure className={`m-0 ${chrome ? "mt-7 border border-[var(--rule)]" : ""}`}>
        {chrome ? (
          <ExhibitHeader
          left="Exhibit — a Session scoped to a saved View"
          right="Real grid · real mention styles"
        />
        ) : null}
        <ProductBridge className={chrome ? "p-3 sm:p-5" : "p-0"}>
          <div className="min-w-0 overflow-hidden border border-border bg-card">
            <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3">
              <p className="min-w-0 truncate text-sm font-medium text-foreground">
                Renewal risk review
              </p>
              <span className="hidden shrink-0 text-[11px] text-muted-foreground sm:inline">
                · {CHANNEL_FIXTURE.name}
              </span>
              <div className="ml-auto flex shrink-0 items-center">
                <ProvenanceAvatarStack
                  actor={MAYA}
                  interfaceType="web"
                  usedTwin
                  action="started"
                />
              </div>
            </header>
            <div className="grid min-w-0 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
              <div className="flex min-w-0 flex-col border-b border-border lg:border-r lg:border-b-0">
                <div className="flex-1 space-y-4 px-4 py-4">
                  <div className="flex justify-end">
                    <p
                      className="m-0 w-fit max-w-[90%] text-sm leading-relaxed break-words"
                      style={{
                        background: "hsl(var(--card))",
                        color: "hsl(var(--foreground))",
                        borderRadius: "18px 18px 4px 18px",
                        padding: "8px 14px",
                      }}
                    >
                      Across <Mention type="view">@Renewal command</Mention> —
                      what's at risk in the next 30 days?
                    </p>
                  </div>
                  <div>
                    <OneShotWipeAnswer source={VIEW_SESSION_ANSWER_MD} />
                    <RunSourcesUsedView
                      className="mt-3"
                      sources={RECEIPT_SOURCES}
                      plugins={RECEIPT_PLUGINS}
                    />
                  </div>
                </div>
                <div className="border-t border-border px-3 py-2.5">
                  <div className="mb-2 flex items-center justify-between gap-3 text-[11px] leading-4 text-muted-foreground">
                    <span className="inline-flex h-6 items-center rounded-md border border-border/70 bg-surface px-2 font-medium">
                      {RECEIVABLES_RECEIPT.runLabel}
                    </span>
                    <span>Help improve this answer</span>
                  </div>
                  <div className="flex items-center gap-2 rounded-lg border border-input bg-background px-3 py-2">
                    <p className="m-0 min-w-0 flex-1 truncate text-sm text-muted-foreground">
                      Ask about <Mention type="view">@Renewal command</Mention>
                      {" — or mention another View"}
                    </p>
                    <span className="shrink-0 text-[10px] font-medium tracking-[0.08em] text-muted-foreground uppercase">
                      Auto
                    </span>
                    <span
                      aria-hidden
                      className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--solid))] text-[hsl(var(--solid-foreground))]"
                    >
                      <ArrowUpIcon className="size-3.5" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex min-w-0 flex-col bg-surface">
                <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border px-3">
                  <Table2Icon
                    className="size-3.5 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <p className="min-w-0 truncate text-xs font-medium text-foreground">
                    Renewal command
                  </p>
                  <div className="ml-auto flex shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground">
                    <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-foreground">
                      Table
                    </span>
                    <span className="px-1">Board</span>
                    <span className="px-1">Dashboard</span>
                  </div>
                  <XIcon
                    className="size-3.5 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <RecordTableGrid
                    table={table}
                    scrollRef={scrollRef}
                    empty={false}
                  />
                </div>
                <p className="border-t border-border px-3 py-1.5 text-[11px] leading-4 text-muted-foreground">
                  Renewal accounts · 12 records · scope pinned to this Session
                </p>
              </div>
            </div>
          </div>
        </ProductBridge>
        {chrome ? (
          <ExhibitFooter>
          The grid is the application's RecordTableGrid over the mentioned View's records; the mention chips carry the product's own MENTION_ENTITY_STYLES; the answer is set in the product's typeset with its real receipt row; the provenance avatars are the real component. The session chrome, composer and panel frame are drawn after the product's session and workspace-resource-panel surfaces, which are live-coupled. Figures echo the fixture run.
        </ExhibitFooter>
        ) : null}
      </figure>
    </section>
  );
}

/* ================================================================= */
/*  CHV-009 · CHV-001 in the application's dark blue                  */
/* ================================================================= */

function DarkChannelExhibit() {
  const tableView = CHANNEL_FIXTURE.views[0];
  return (
    <section>
      <ConceptHeader
        marker="Channels · dark appearance"
        title="The same surface, after dark."
        body="CHV-001's channel workspace on the application's own dark-blue theme — the .dark tokens with the theme-dark-blue accents, transcribed verbatim, nothing re-inked. The sheet asks one question: does the product's night surface, with its blue primary, sit comfortably inside the claret-accented editorial frame?"
        reading="One product, day and night."
      />

      <figure className="m-0 mt-7 border border-[var(--rule)]">
        <ExhibitHeader
          left="Exhibit — channel workspace, dark blue"
          right="App theme · trial"
        />
        <ContextLedger view={`${tableView.name} · ${tableView.kind}`} />
        <ProductBridge
          appearance="dark-blue"
          className="border-t border-dotted border-[var(--rule)] p-3 sm:p-5"
        >
          <div className="min-w-0 border border-border bg-card">
            <ChannelToolbar state="fresh" />
            <ChannelSectionNav selected="Data sets" />
            <ViewPickerBar name={tableView.name} kind={tableView.kind} />
            <RecordTable dark />
          </div>
        </ProductBridge>
        <ExhibitFooter>
          Every colour inside the plate is the application's own dark appearance — the .dark block with the theme-dark-blue overrides, applied through the bridge with no editorial re-inking; the grid, freshness indicator and popover are the same real components as CHV-001. The health chips use the product's dark option colours. The frame, ledger and captions around it stay editorial, which is the comparison this variant exists to make.
        </ExhibitFooter>
      </figure>
    </section>
  );
}

/* ================================================================= */
/*  CHV-010 · The whole section, after dark                           */
/* ================================================================= */

function DarkSectionExhibit() {
  const tableView = CHANNEL_FIXTURE.views[0];
  return (
    <section>
      <ConceptHeader
        dark
        marker="Channels · after dark"
        title="The whole section, after dark."
        body="The whole-dark trial, kept for reference: the section on the board's dark surface with the app's night plate inside it. The settled idiom went the other way — marketing sections stay light on the canonical ground, palette darks stay editorial, and the product asset alone carries the night (SES-005, CHV-011)."
        reading="The whole-dark reference."
      />

      <figure className="m-0 mt-7 border border-[var(--rule)]">
        <ExhibitHeader
          dark
          left="Exhibit — channel workspace, dark on dark"
          right="App theme · editorial night"
        />
        <ContextLedger dark view={`${tableView.name} · ${tableView.kind}`} />
        <ProductBridge
          appearance="dark"
          className="border-t border-dotted border-[var(--rule)] p-3 sm:p-5"
        >
          <div className="min-w-0 border border-border bg-card">
            <ChannelToolbar state="fresh" />
            <ChannelSectionNav selected="Data sets" />
            <ViewPickerBar name={tableView.name} kind={tableView.kind} />
            <RecordTable dark />
          </div>
        </ProductBridge>
        <ExhibitFooter dark>
          Kept as the whole-dark reference. The plate is the application's own dark appearance through the bridge; the section is the board's dark surface in the active palette. The settled idiom (SES-005, CHV-011) keeps sections light instead and lets the asset carry the night.
        </ExhibitFooter>
      </figure>
    </section>
  );
}

/* ================================================================= */
/*  CHV-008 · The Board, under the question                           */
/* ================================================================= */

/**
 * Stage accents exactly as the product's columnColor() assigns them for these
 * five option labels (record-lens-utils.ts — no keyword match, so the index
 * palette applies in option order).
 */
const STAGE_ACCENTS: Record<RenewalRecord["stage"], string> = {
  Discovery: "#0ea5e9",
  Validation: "#8b5cf6",
  Commercial: "#14b8a6",
  Contracting: "#ec4899",
  Renewed: "#6366f1",
};

/** Column subtotals as the product prints them (en-GB, no decimals). */
const BOARD_SUBTOTAL_FORMAT = new Intl.NumberFormat("en-GB", {
  maximumFractionDigits: 0,
});

function stageSubtotal(stage: RenewalRecord["stage"]): string | null {
  const entry = RENEWAL_DASHBOARD.valueByStage.find(
    (item) => item.stage === stage,
  );
  return entry ? BOARD_SUBTOTAL_FORMAT.format(entry.value) : null;
}

function SessionWithBoardExhibit({ dark = false }: { dark?: boolean }) {
  return (
    <section>
      <ConceptHeader
        marker={dark ? "Sessions · Views · Board · after dark" : "Sessions · Views · Board"}
        title="The pipeline, open under the question."
        body="Mention the Board view and one scope drives both readings: the agent answers over the records, and the columns your team drags sit right below the conversation. Same Data Set, same pinned scope — the answer and the board cannot disagree, because there is nothing between them to drift."
        reading="Ask over the board you work."
      />

      <figure className="m-0 mt-7 border border-[var(--rule)]">
        <ExhibitHeader
          left="Exhibit — a Session with the Board view open"
          right={dark ? "Real kanban · after dark" : "Real kanban · real mention styles"}
        />
        <ProductBridge appearance={dark ? "dark" : "light"} className="p-3 sm:p-5">
          <div className="min-w-0 overflow-hidden border border-border bg-card">
            <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3">
              <p className="min-w-0 truncate text-sm font-medium text-foreground">
                Pipeline weight check
              </p>
              <span className="hidden shrink-0 text-[11px] text-muted-foreground sm:inline">
                · {CHANNEL_FIXTURE.name}
              </span>
              <div className="ml-auto flex shrink-0 items-center">
                <ProvenanceAvatarStack
                  actor={SAM}
                  interfaceType="web"
                  usedTwin
                  action="started"
                />
              </div>
            </header>

            <div className="px-4 py-4">
              <div className="flex justify-end">
                <p
                  className="m-0 w-fit max-w-[90%] text-sm leading-relaxed break-words"
                  style={{
                    background: "hsl(var(--card))",
                    color: "hsl(var(--foreground))",
                    borderRadius: "18px 18px 4px 18px",
                    padding: "8px 14px",
                  }}
                >
                  Across <Mention type="view">@Renewal stages</Mention> — where
                  is the pipeline heaviest right now?
                </p>
              </div>
              <div className="mt-4">
                <div className="typeset typeset-chat max-w-[62ch] break-words whitespace-normal">
                  <p>
                    <strong>Commercial</strong> is the heaviest column —
                    £256,000 across three accounts, led by Lumen Mobility at
                    £125,000. Validation follows at £245,000.
                  </p>
                  <p>
                    In Contracting, £84,000 of the £180,000 —{" "}
                    <strong>Northstar Gridworks</strong> — is At risk; the rest
                    is Brightwater Energy, on track for 22 September.
                  </p>
                </div>
                <RunSourcesUsedView
                  className="mt-3"
                  sources={RECEIPT_SOURCES}
                  plugins={RECEIPT_PLUGINS}
                />
              </div>
            </div>

            <div className="flex h-9 shrink-0 items-center gap-2 border-y border-border bg-surface px-3">
              <KanbanSquareIcon
                className="size-3.5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <p className="min-w-0 truncate text-xs font-medium text-foreground">
                Renewal stages
              </p>
              <span className="hidden shrink-0 text-[11px] text-muted-foreground sm:inline">
                Referenced view · pinned to this Session
              </span>
              <div className="ml-auto flex shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground">
                <span className="px-1">Table</span>
                <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-foreground">
                  Board
                </span>
                <span className="px-1">Dashboard</span>
              </div>
            </div>
            <div className="flex h-[480px] min-w-0 gap-2 overflow-x-auto bg-surface p-2">
              {STAGE_ORDER.map((stage) => {
                const records = RENEWAL_RECORDS.filter(
                  (record) => record.stage === stage,
                );
                return (
                  <BoardColumnView
                    key={stage}
                    title={stage}
                    color={STAGE_ACCENTS[stage]}
                    count={records.length}
                    subtotal={stageSubtotal(stage)}
                  >
                    <ul className="m-0 list-none p-0">
                      {records.map((record) => (
                        <li key={record.title} className="pb-2">
                          <BoardCardView
                            title={record.title}
                            sumText={record.annualValue}
                            chips={
                              <>
                                <span className="truncate">
                                  {record.probability}
                                </span>
                                <span className="truncate">
                                  {record.accountLead}
                                </span>
                                <span className="truncate">
                                  {record.renewalDate}
                                </span>
                              </>
                            }
                          />
                        </li>
                      ))}
                    </ul>
                  </BoardColumnView>
                );
              })}
            </div>

            <div className="border-t border-border px-3 py-2.5">
              <div className="flex items-center gap-2 rounded-lg border border-input bg-background px-3 py-2">
                <p className="m-0 min-w-0 flex-1 truncate text-sm text-muted-foreground">
                  Ask anything about these records…
                </p>
                <span className="shrink-0 text-[10px] font-medium tracking-[0.08em] text-muted-foreground uppercase">
                  Auto
                </span>
                <span
                  aria-hidden
                  className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--solid))] text-[hsl(var(--solid-foreground))]"
                >
                  <ArrowUpIcon className="size-3.5" />
                </span>
              </div>
            </div>
          </div>
        </ProductBridge>
        <ExhibitFooter>
          {`The columns and cards are the application's own BoardColumnView and BoardCardView, styled by its imported kanban.css, with accents exactly as the product's columnColor() assigns them; virtualization, drag-and-drop and record actions stay in the application. The mention chip, receipt avatars, provenance stack and typeset are real; the chrome and composer are drawn after the product's session surface. The board scrolls sideways exactly as it does in the product. Figures echo the fixture run.${dark ? " The appearance is the application's own dark blue on the canonical night — the asset keeps its dark while the section keeps the light ground." : ""}`}
        </ExhibitFooter>
      </figure>
    </section>
  );
}

/* ================================================================= */
/*  CHV-007 · The feed, with the Twin in the thread                   */
/* ================================================================= */

const POST_A_ENGAGEMENT: ChannelFeedEngagement = {
  commentCount: 2,
  unreadCount: 0,
  participantIds: [],
  reactions: [
    { reaction: "thumbs_up", count: 2, reactedByViewer: false, actors: [] },
    { reaction: "eyes", count: 1, reactedByViewer: false, actors: [] },
  ],
};

const POST_B_ENGAGEMENT: ChannelFeedEngagement = {
  commentCount: 0,
  unreadCount: 0,
  participantIds: [],
  reactions: [
    { reaction: "check", count: 1, reactedByViewer: false, actors: [] },
  ],
};

function FeedRow({
  actor,
  name,
  time,
  children,
  engagement,
  thread,
}: {
  actor: ProvenanceActor;
  name: string;
  time: string;
  children: ReactNode;
  engagement?: ChannelFeedEngagement;
  thread?: ReactNode;
}) {
  return (
    <article className="px-3.5 py-3">
      <div className="flex items-start gap-2.5">
        <ProvenanceAvatarStack actor={actor} action="posted" className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-[12px] leading-4">
            <span className="font-semibold text-foreground">{name}</span>{" "}
            <span className="text-muted-foreground">· {time}</span>
          </p>
          <div className="mt-1 max-w-[72ch] text-sm leading-relaxed text-foreground">
            {children}
          </div>
          {engagement ? (
            <ChannelFeedEngagementSummary
              engagement={engagement}
              onOpen={() => {}}
            />
          ) : null}
        </div>
      </div>
      {thread ? (
        <div className="mt-2.5 ml-5 grid gap-3 border-l border-border/60 pl-4">
          {thread}
        </div>
      ) : null}
    </article>
  );
}

function FeedComment({
  actor,
  name,
  time,
  children,
}: {
  actor: ProvenanceActor;
  name: string;
  time: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <ProvenanceAvatarStack actor={actor} action="posted" className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="text-[12px] leading-4">
          <span className="font-semibold text-foreground">{name}</span>{" "}
          <span className="text-muted-foreground">· {time}</span>
        </p>
        <div className="mt-1 max-w-[66ch] text-[13px] leading-relaxed text-foreground">
          {children}
        </div>
      </div>
    </div>
  );
}

export function FeedExhibit({ chrome = true }: { chrome?: boolean } = {}) {
  return (
    <section>
      {chrome ? (
        <ConceptHeader
        marker="Channels · Feed"
        title="The discussion, where the records are."
        body="Every channel carries a feed: your team posts updates, comments in threads, reacts — and the Twin is a member. Mention @MLX in a thread and it answers there, with governed figures and its receipt. After a sync or an automation it posts its own updates, so the discussion and the data never live in different places."
        reading="Ask in the thread. Governed answer in the thread."
      />
      ) : null}

      <figure className={`m-0 ${chrome ? "mt-7 border border-[var(--rule)]" : ""}`}>
        {chrome ? (
          <ExhibitHeader
          left="Exhibit — posts, a thread, and the Twin replying"
          right="Real reactions · avatars · receipt"
        />
        ) : null}
        <ProductBridge className={chrome ? "p-3 sm:p-5" : "p-0"}>
          <div className="min-w-0 overflow-hidden border border-border bg-card">
            <ChannelToolbar state="fresh" />
            <ChannelSectionNav selected="Feed" />
            <div className="border-b border-border/60 px-3.5 py-2.5">
              <div className="flex items-center gap-2.5">
                <ProvenanceAvatarStack actor={MAYA} action="posted" />
                <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-input bg-background px-3 py-1.5">
                  <p className="m-0 min-w-0 flex-1 truncate text-sm text-muted-foreground">
                    Share an update with the channel…
                  </p>
                  <span
                    aria-hidden
                    className="flex size-5 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--solid))] text-[hsl(var(--solid-foreground))]"
                  >
                    <ArrowUpIcon className="size-3" />
                  </span>
                </div>
              </div>
            </div>
            <div className="divide-y divide-border/50">
              <FeedRow
                actor={MAYA}
                name="Maya Chen"
                time="Today, 09:12"
                engagement={POST_A_ENGAGEMENT}
                thread={
                  <>
                    <FeedComment actor={SAM} name="Sam Okafor" time="09:31">
                      <p className="m-0">
                        <Mention type="teammate">@MLX</Mention> if Northstar
                        slips past quarter end, where does committed Q3 land?
                      </p>
                    </FeedComment>
                    <FeedComment actor={TWIN_ACTOR} name="MLX" time="09:31">
                      <p className="m-0">
                        £84,000 would leave the quarter. Renewed so far stays at{" "}
                        <strong>£79,000</strong> — Orbit Education and Alder
                        &amp; Co Logistics — with Brightwater Energy's £96,000
                        still to land on 22 September.
                      </p>
                      <RunSourcesUsedView
                        className="mt-2"
                        sources={RECEIPT_SOURCES}
                        plugins={RECEIPT_PLUGINS}
                      />
                    </FeedComment>
                  </>
                }
              >
                <p className="m-0">
                  Pushed the Northstar decision call to Thursday — legal wants
                  one more pass on the DPA before we counter.
                </p>
              </FeedRow>
              <FeedRow
                actor={TWIN_ACTOR}
                name="MLX"
                time="Today, 06:54"
                engagement={POST_B_ENGAGEMENT}
              >
                <p className="m-0">
                  Refreshed aged receivables after this morning's Xero sync —
                  £21,541.38 outstanding, 42.8% overdue. Collection priority is
                  still Ridgeway University and Port &amp; Philip Freight.
                </p>
              </FeedRow>
            </div>
          </div>
        </ProductBridge>
        {chrome ? (
          <ExhibitFooter>
          The reaction chips and reply control are the application's own ChannelFeedEngagementSummary, the avatars are its ProvenanceAvatarStack (the Twin renders its real mark), the mention chip carries the product's mention styles and the receipt row is real. The post and thread framing is drawn after the product's feed rows, which are live-coupled. People and figures echo the fixture run; the accounts are fictional.
        </ExhibitFooter>
        ) : null}
      </figure>
    </section>
  );
}

/* ================================================================= */

export const CHANNEL_GROUP: ConceptGroup = {
  id: "channels",
  prefix: "CHV",
  title: "Channels, Data Sets and views",
  blurb:
    "The workspace surface where governed records are read — and the schema discipline that keeps AI output manageable: one channel, one Data Set, saved views over it, and typed write operations every agent must file through. The Table view and the Dashboard charts are the application's own components imported through the bridge; the chrome and the Board are reconstructions from the same fixtures, all carrying the product's tokens and typeface on the editorial ground.",
  specs: [
    {
      id: "CHV-001",
      title: "One channel, one governed Data Set",
      note: "The channel workspace with its toolbar, freshness state, section nav and the Data Set rendered through the application's real table grid — with channel, Data Set and view named above it.",
      surface: "ground",
      width: "contained",
      interaction: "prototype",
      state: "Table view",
      render: () => <ChannelWorkspaceExhibit />,
    },
    {
      id: "CHV-002",
      title: "One Data Set, many views",
      note: "The same twelve records rendered three ways — Table, Board grouped by stage, and Dashboard on the product's real chart widgets — from one fixture array, so the claim is demonstrated rather than asserted.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      state: "Table · Board · Dashboard",
      render: () => <ManyViewsExhibit />,
    },
    {
      id: "CHV-003",
      title: "Know how old the number is",
      note: "The channel freshness surface: the real indicator and its popover on the toolbar, the popover rows printed for reading, and a contrasting stale state with its own reason code.",
      surface: "ground",
      width: "contained",
      interaction: "prototype",
      state: "Fresh · stale",
      render: () => <FreshnessExhibit />,
    },
    {
      id: "CHV-004",
      title: "Stop the sprawl at the schema",
      note: "The same week of agent output twice: drawn slips of unstructured chat-and-notes sprawl beside the real grid of twelve typed records — the maintainability argument, demonstrated.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      state: "Sprawl · schema",
      render: () => <SchemaVsSprawlExhibit />,
    },
    {
      id: "CHV-005",
      title: "The schema is the contract",
      note: "One object type and its write path: the renewal schema beside the real twin_channel_* operations and scopes every agent must file through — transcribed from the contract source.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      state: "Write path",
      render: () => <SchemaContractExhibit />,
    },
    {
      id: "CHV-006",
      title: "The View, in the Session",
      note: "A Session scoped by mentioning @Renewal command in the composer — real mention styles, the real grid open beside the conversation, and a governed answer over exactly those records.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      state: "Session · View",
      render: () => <SessionWithViewExhibit />,
    },
    {
      id: "CHV-007",
      title: "The feed, with the Twin in the thread",
      note: "Posts, reactions and a comment thread on the channel: mention @MLX and it answers in-thread with a governed figure and its receipt; after a sync it posts its own update.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      state: "Posts · comments",
      render: () => <FeedExhibit />,
    },
    {
      id: "CHV-008",
      title: "The Board, under the question",
      note: "The same Session idiom with the Board view open beneath the answer — real kanban columns and cards on the product's own kanban.css, accents as columnColor() assigns them, one pinned scope for both readings.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      state: "Session · Board",
      render: () => <SessionWithBoardExhibit />,
    },
    {
      id: "CHV-009",
      title: "One channel, in the application's dark blue",
      note: "CHV-001's workspace on the app's own .dark + theme-dark-blue tokens, verbatim and un-re-inked — the trial of the product's night surface inside the claret editorial frame.",
      surface: "ground",
      width: "contained",
      interaction: "prototype",
      state: "Dark blue",
      variantOf: "CHV-001",
      render: () => <DarkChannelExhibit />,
    },
    {
      id: "CHV-010",
      title: "The whole section, after dark",
      note: "CHV-009's plate on the board's deep surface: editorial claret dark framing the application's dark blue — the two darks judged as one section, the way a live product-after-dark band would render.",
      surface: "dark",
      width: "contained",
      interaction: "prototype",
      state: "Dark · dark",
      variantOf: "CHV-009",
      render: () => <DarkSectionExhibit />,
    },
    {
      id: "CHV-011",
      title: "The Board, after dark",
      note: "CHV-008's full session-and-kanban sheet with the asset after dark: the application's dark blue on the canonical night, floating in the light marketing section — real columns, cards and mention styles unchanged underneath.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      state: "Board · night",
      variantOf: "CHV-008",
      render: () => <SessionWithBoardExhibit dark />,
    },
  ],
};
